import { Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import { logger } from '@services/context';
import { toastifyService } from '@services/ToastifyService';
import { IconComponent } from '@shared/components/IconComponent';
import Popup, { PopupProps } from '@shared/components/Popup';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { Dashboard } from '@shared/protos/v2/dashboard';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import { useSharedDashboards } from '../../appbar/useSharedDashboards';
import { useDashboardOrder } from '../../hooks/useDashboardOrder';
import { CustomListItem, DashboardListItem, SharedDashboardListItem } from '../CustomListItems';
import { ShareDashboardDialog } from '../ShareDashboardDialog';
import DeleteDashboard from './DeleteDashboard';
import EditDashboard from './EditDashboard';

type Props = Omit<PopupProps, 'children'> & {
  onClose: () => void;
  isPremiumUser: boolean;
};

type DashboardsProps = {
  onClose?: () => void;
  isPremiumUser: boolean;
};

type Mode = 'view' | 'edit' | 'delete' | 'share';

const UserDashboards = ({ onClose, isPremiumUser }: DashboardsProps) => {
  const [mode, setMode] = useState<Mode>('view');
  const [loading, setLoading] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard>();
  const { addDashboard, allDashboards } = useDashboardContext();
  const { sharedDashboards, manageSharedDashboard } = useSharedDashboards();
  const { sortedDashboardList } = useDashboardOrder(allDashboards);
  const navigate = useNavigate();

  const handleOpenDashboard = ({ id }: Dashboard) => {
    onClose?.();
    navigate(`/dashboard/${id}`);
  };

  const handleOpenFreemiumNews = () => {
    onClose?.();
    navigate('/freemium-news');
  };

  const handleNewDashboardClick = useCallback(async () => {
    try {
      setLoading(true);
      const dashboard = await addDashboard();
      onClose?.();
      navigate(`/dashboard/${dashboard.id}`);
    } catch (error: any) {
      toastifyService.showErrorMessage('Unable to create dashboard, please try again');
      logger.error('Error creating dashboard', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleModeChange = (mode: Mode, dashboard: Dashboard) => {
    setMode(mode);
    setSelectedDashboard(dashboard);
  };

  const onDelete = (close?: boolean) => {
    setMode('view');
    close && onClose?.();
  };

  const handleBack = () => setMode('view');

  if (mode === 'edit' && selectedDashboard) {
    return <EditDashboard dashboard={selectedDashboard} onBack={handleBack} />;
  }

  if (mode === 'delete' && selectedDashboard) {
    return <DeleteDashboard dashboard={selectedDashboard} onDelete={onDelete} onBack={handleBack} />;
  }

  if (mode === 'share' && selectedDashboard) {
    return <ShareDashboardDialog isOpen={true} dashboard={selectedDashboard} onClose={handleBack} />;
  }

  return (
    <SimpleBar style={{ maxHeight: '275px', minWidth: '240px' }}>
      <Stack sx={{ paddingBottom: 2 }}>
        <List disablePadding style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <CustomListItem
            primary={
              <Stack direction="row" gap={1} style={{ alignItems: 'center' }}>
                <IconComponent name="ri-newspaper-line" />
                Freemium News
              </Stack>
            }
            primaryTypographyProps={{
              fontWeight: 'fontWeightBold',
              color: 'primary.main',
            }}
            onClick={handleOpenFreemiumNews}
          />
          <Divider style={{ marginBottom: 5 }} />
          {isPremiumUser && (
            <>
              <CustomListItem
                primary={
                  <Stack direction="row" gap={1} style={{ alignItems: 'center' }}>
                    <IconComponent name="ri-add-circle-line" />
                    New Dashboard
                  </Stack>
                }
                primaryTypographyProps={{
                  fontWeight: 'fontWeightBold',
                  color: 'primary.main',
                }}
                onClick={handleNewDashboardClick}
                loading={loading}
              />
              <Divider style={{ marginTop: 5 }} />
            </>
          )}
          {isPremiumUser
            ? sortedDashboardList.map(dashboard => (
                <DashboardListItem
                  key={dashboard.id}
                  dashboardId={dashboard.id}
                  label={dashboard.name}
                  onClick={() => handleOpenDashboard(dashboard)}
                  onEdit={() => handleModeChange('edit', dashboard)}
                  onShare={() => handleModeChange('share', dashboard)}
                  onDelete={() => handleModeChange('delete', dashboard)}
                />
              ))
            : null}
          {isPremiumUser && sharedDashboards.length > 0 && (
            <>
              <Divider style={{ marginTop: 5 }} />
              <Typography variant="h6" textAlign="center">
                Dashboards shared with you
              </Typography>
              <Divider style={{ marginBottom: 5 }} />
              {sharedDashboards.map(dashboard => (
                <SharedDashboardListItem
                  key={dashboard.id}
                  dashboardId={dashboard.id}
                  label={dashboard.dashboard_name}
                  onAccept={() => manageSharedDashboard(dashboard.id, true)}
                  onReject={() => manageSharedDashboard(dashboard.id, false)}
                />
              ))}
            </>
          )}
        </List>
      </Stack>
    </SimpleBar>
  );
};

export const DashboardsPopup = (props: Props) => {
  const { isMobile } = useScreenSize();

  return (
    <Popup offset={[0, isMobile ? 20 : 5]} arrow={isMobile} {...props}>
      <UserDashboards onClose={props.onClose} isPremiumUser={props.isPremiumUser} />
    </Popup>
  );
};
